import {
    createContext,
    type ForwardedRef,
    type ReactNode,
    useCallback,
    useContext,
    useEffect,
    useMemo,
    useRef,
    useState,
} from "react";

interface IHeaderShadowContextValue {
    isContentScrolled: boolean;
    setIsContentScrolled: (value: boolean) => void;
}

const HeaderShadowContext = createContext<IHeaderShadowContextValue>({
    isContentScrolled: false,
    setIsContentScrolled: () => {},
});

/**
 * Holds shared "has the scroll container scrolled past the top" state. Wrap a
 * `Frame` (or any scroll-container + sticky-header composition) once.
 * Descendants call `useHeaderShadow()` to either attach the ref to the scroll
 * container or read the boolean.
 */
export function HeaderShadowProvider({ children }: { children: ReactNode }) {
    const [isContentScrolled, setIsContentScrolled] = useState(false);
    const value = useMemo(() => ({ isContentScrolled, setIsContentScrolled }), [isContentScrolled]);
    return <HeaderShadowContext.Provider value={value}>{children}</HeaderShadowContext.Provider>;
}

/**
 * Drives the "drop shadow on a sticky header once content scrolls" effect.
 *
 * Returns:
 * - `scrollContainerRef` — attach as `ref` on the scrollable element. The hook
 *   adds a passive `scroll` listener and flips the shared boolean when
 *   `scrollTop > 0`. If `forwardedRef` is passed, the node is also forwarded
 *   to it.
 * - `isContentScrolled` — read from the sticky header to toggle a shadow class.
 *
 * Multiple components in the same `HeaderShadowProvider` may call this hook;
 * each gets its own ref but they share the boolean. Consumers that only read
 * the boolean can ignore `scrollContainerRef` — the listener is inert until a
 * node is attached.
 *
 * @param forwardedRef Optional ref the caller wants to forward to the scroll
 *  container element (typically `forwardRef`'s second argument).
 */
export function useHeaderShadow<T extends HTMLElement = HTMLElement>(
    forwardedRef?: ForwardedRef<T>,
) {
    const { isContentScrolled, setIsContentScrolled } = useContext(HeaderShadowContext);
    const scrollContainerEl = useRef<T | null>(null);

    const scrollContainerRef = useCallback(
        (node: T | null) => {
            scrollContainerEl.current = node;
            if (typeof forwardedRef === "function") {
                forwardedRef(node);
            } else if (forwardedRef) {
                // Standard ref-forwarding pattern — assigning to .current of a
                // RefObject is the React-blessed way to mutate the consumer's
                // ref, even though the type technically marks it readonly.
                // eslint-disable-next-line react-hooks/immutability
                forwardedRef.current = node;
            }
        },
        [forwardedRef],
    );

    useEffect(() => {
        const el = scrollContainerEl.current;
        if (!el) return;
        const onScroll = () => setIsContentScrolled(el.scrollTop > 0);
        el.addEventListener("scroll", onScroll, { passive: true });
        // Initial sync — handles cases like restored scroll position on
        // refresh, where scrollTop is non-zero at mount.
        onScroll();
        return () => el.removeEventListener("scroll", onScroll);
    }, [setIsContentScrolled]);

    return { scrollContainerRef, isContentScrolled };
}
